/**
 * The ink the opening band writes in when it is wearing the slideshow.
 *
 * Its own module, not constants hanging off a component file: a file that
 * exports a component may not also export a value without breaking fast
 * refresh — the same reason `hoverSpring` and `revealMotion` sit apart from
 * the components that use them.
 *
 * Every value here is stated rather than taken from the palette, because the
 * band is dark in *both* colour schemes. A scheme-following slot is the wrong
 * shape on a surface that does not follow the scheme — the same bargain
 * `Section tone="inverse"` and the `aurora` pattern already make.
 */

export const INVERSE_TITLE = '#FFFFFF';
export const INVERSE_LEDE = 'rgba(255,255,255,0.80)';
export const INVERSE_LABEL = 'rgba(255,255,255,0.86)';

/**
 * The accent red.
 *
 * `primary.light` resolves to rgb(208,99,109) in light mode, which measures
 * 3.1:1 against the scrimmed band — it cleared the bar only while the band was
 * near-black. This is the dark palette's `primaryHover`, which measures 5.1:1
 * in light mode and 7.6:1 in dark, and carries the icons past the 3:1 that
 * non-text contrast asks for (WCAG 1.4.11) as well.
 */
export const INVERSE_ACCENT = '#F0929B';

/**
 * The same band, written on glass.
 *
 * The `clear` panel is not a darker surface — it is the band itself, hazed and
 * lifted on the way through the frost — so the ink that clears 4.5:1 on the
 * band does not automatically clear it inside the panel, and in this case it
 * is the *lifting* that costs the margin. Both values below are measured
 * against the pane's brightest interior over this band, which is the 99th
 * percentile of the backdrop with a car passing behind it; theme/glass.ts
 * carries the arithmetic and the rest of the stack.
 *
 * INVERSE_LABEL at 0.86 white lands at 3.8:1 there, so the labels on glass are
 * pure white (4.8:1). INVERSE_ACCENT at #F0929B lands at 2.1:1, well under the
 * 3:1 that non-text contrast asks for, so the icons on glass take a paler pink
 * (3.2:1). It is the brighter accent the reference glass uses anyway — a pane
 * this frosted wants a high-luminance accent, not a mid-tone one.
 */
export const GLASS_LABEL = '#FFFFFF';
export const GLASS_ACCENT = '#F8C7CC';

/**
 * The hairline between claims in the trust strip.
 *
 * Decorative in the strict sense — it separates items that are already
 * separated by a gap and by an icon, so it carries no meaning of its own and
 * is exempt from 1.4.11. It is set low on purpose: a rule bright enough to
 * measure against the pane would draw more attention than the labels it
 * divides, which is the failure the old floating panel had at a larger scale.
 */
export const STRIP_RULE = 'rgba(255,255,255,0.22)';

/**
 * The same values again, stated across the breakpoint where the stage appears.
 *
 * Everything above this line is ink for a dark surface, and until the band
 * became a title block on phones that was the only kind of ink it needed: a
 * boolean said whether the band was wearing the slideshow, and the boolean
 * chose between a stated white and a palette slot. That no longer answers the
 * question, because the band is now dark at one width and not at another, and
 * a boolean cannot hold two answers.
 *
 * So the boolean chooses between these objects instead of between two strings.
 * Each one is the palette's own slot below `md`, where the band sits on
 * `background.default` and the scheme is in charge, and the stated inverse
 * value from `md` up, where it is not. MUI resolves a palette path per
 * breakpoint, so `{ xs: 'text.primary', md: '#FFFFFF' }` is a real colour at
 * both ends rather than a string that happens to parse.
 *
 * Why these have to be responsive values and not a second ink module: the
 * prerender. With `ssr: false` the HTML is written at build time for no
 * particular width, so a component cannot ask how wide the screen is before
 * choosing a colour — if it did, every phone would receive white-on-white in
 * its static HTML and repaint after hydration. The breakpoint has to survive
 * in the stylesheet, which means it has to be in the value.
 */

/** The eyebrow over the headline. */
export const BAND_EYEBROW_INK = { xs: 'primary.main', md: INVERSE_ACCENT };

/** The h1. */
export const BAND_TITLE_INK = { xs: 'text.primary', md: INVERSE_TITLE };

/** The lede under it. */
export const BAND_LEDE_INK = { xs: 'text.secondary', md: INVERSE_LEDE };

/**
 * A proof point's mark and its label.
 *
 * The `md` halves are the glass values rather than the band values, because
 * from `md` up these are read inside the frosted pane and not on the band
 * itself — see the measurements above. Below `md` there is no pane: the claim
 * is a bordered chip on `background.paper`, so the label takes the palette's
 * secondary text and the mark takes the brand red, which is the pairing every
 * other light surface on the site uses.
 */
export const RAIL_ACCENT_INK = { xs: 'primary.main', md: GLASS_ACCENT };
export const RAIL_LABEL_INK = { xs: 'text.secondary', md: GLASS_LABEL };

/**
 * The trail above the eyebrow, and the current page within it.
 *
 * Breadcrumbs keeps the stated inverse pair it has always used; what is new is
 * that the pair only applies from `md` up. Below that the trail is on the
 * page's surface like every other trail on the site (the vehicle page's, the
 * guide article's), so it takes the same two palette slots they do.
 */
export const CRUMB_LINK_INK = { xs: 'text.secondary', md: 'rgba(255,255,255,0.70)' };
export const CRUMB_CURRENT_INK = { xs: 'text.primary', md: 'rgba(255,255,255,0.94)' };
